import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconBack, IconTrash } from '../../components/icons';
import { labelFor, listOptions, useConfig } from '../../context/ConfigContext';
import { useConfirm } from '../../context/ConfirmContext';
import { formatMoney } from '../../utils/format';

interface Beneficiary {
  id: number;
  beneficiary_name: string;
  relationship: string;
  allocation_percentage: string;
  beneficiary_type: 'primary' | 'contingent';
}

interface DependentLink {
  id: number;
  coverage_start: string;
  coverage_end: string | null;
  dependent: { id: number; name: string; relationship: string };
}

interface EmployeeDependentOption {
  id: number;
  name: string;
}

interface EnrollmentDetailRecord {
  id: number;
  coverage_start: string;
  coverage_end: string | null;
  employee_contribution: string;
  employer_contribution: string;
  status: 'active' | 'ended';
  plan: { plan_name: string; type: { type_name: string }; provider: { provider_name: string } };
  coverage_option: { option_name: string };
  beneficiaries: Beneficiary[];
  dependent_links: DependentLink[];
  employee_position: { employee: { dependents: EmployeeDependentOption[] } };
}

const TABS = ['details', 'beneficiaries', 'dependents'] as const;
type Tab = (typeof TABS)[number];
const TAB_LABELS: Record<Tab, string> = { details: 'Details', beneficiaries: 'Beneficiaries', dependents: 'Dependents' };

const emptyBeneficiaryForm = { beneficiary_name: '', relationship: '', allocation_percentage: '100', beneficiary_type: 'primary' as 'primary' | 'contingent' };

export function EmployeeBenefitEnrollmentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { lists } = useConfig();
  const confirmDelete = useConfirm();
  const [tab, setTab] = useState<Tab>('details');
  const [enrollment, setEnrollment] = useState<EnrollmentDetailRecord | null>(null);

  const [beneficiaryForm, setBeneficiaryForm] = useState(emptyBeneficiaryForm);
  const [beneficiaryErrors, setBeneficiaryErrors] = useState<Record<string, string[]>>({});

  const [dependentForm, setDependentForm] = useState({ employee_dependent_id: '', coverage_start: '' });
  const [dependentErrors, setDependentErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<EnrollmentDetailRecord>(`/employee-benefit-enrollments/${id}`).then((res) => setEnrollment(res.data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const addBeneficiary = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/employee-benefit-enrollments/${id}/beneficiaries`, beneficiaryForm);
      setBeneficiaryForm(emptyBeneficiaryForm);
      setBeneficiaryErrors({});
      load();
    } catch (err) {
      setBeneficiaryErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removeBeneficiary = async (beneficiary: Beneficiary) => {
    if (!(await confirmDelete(`Remove "${beneficiary.beneficiary_name}"?`))) return;
    await api.delete(`/employee-beneficiaries/${beneficiary.id}`);
    load();
  };

  const addDependentCoverage = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/employee-benefit-enrollments/${id}/dependents`, dependentForm);
      setDependentForm({ employee_dependent_id: '', coverage_start: '' });
      setDependentErrors({});
      load();
    } catch (err) {
      setDependentErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const removeDependentCoverage = async (link: DependentLink) => {
    if (!(await confirmDelete(`Remove "${link.dependent.name}" from this enrollment?`))) return;
    await api.delete(`/employee-benefit-enrollment-dependents/${link.id}`);
    load();
  };

  if (!enrollment) return null;

  const linkedDependentIds = new Set(enrollment.dependent_links.map((link) => link.dependent.id));
  const availableDependents = enrollment.employee_position.employee.dependents.filter((d) => !linkedDependentIds.has(d.id));

  return (
    <>
      <header className="page-header">
        <h1>{enrollment.plan.plan_name}</h1>
        <div className="page-header-actions">
          <IconButton icon={IconBack} label="Back" onClick={() => navigate(-1)} />
        </div>
      </header>

      <div className="toolbar">
        <div className="tab-group">
          {TABS.map((t) => (
            <button key={t} type="button" className={tab === t ? 'tab active' : 'tab'} onClick={() => setTab(t)}>
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="card">
        {tab === 'details' && (
          <div className="detail-grid">
            <div className="detail-item">
              <span className="detail-item-label">Plan</span>
              <span className="detail-item-value">{enrollment.plan.plan_name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Type</span>
              <span className="detail-item-value">{enrollment.plan.type?.type_name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Provider</span>
              <span className="detail-item-value">{enrollment.plan.provider?.provider_name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Coverage Option</span>
              <span className="detail-item-value">{enrollment.coverage_option?.option_name}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Employee Contribution</span>
              <span className="detail-item-value">{formatMoney(Number(enrollment.employee_contribution))}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Employer Contribution</span>
              <span className="detail-item-value">{formatMoney(Number(enrollment.employer_contribution))}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Coverage Start</span>
              <span className="detail-item-value">{enrollment.coverage_start}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Coverage End</span>
              <span className={`detail-item-value${enrollment.coverage_end ? '' : ' empty'}`}>{enrollment.coverage_end ?? 'Ongoing'}</span>
            </div>
            <div className="detail-item">
              <span className="detail-item-label">Status</span>
              <span className="detail-item-value">
                <span className={`badge ${enrollment.status === 'active' ? 'badge-active' : 'badge-cancelled'}`}>{enrollment.status}</span>
              </span>
            </div>
          </div>
        )}

        {tab === 'beneficiaries' && (
          <>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Relationship</th>
                    <th>Allocation</th>
                    <th>Type</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {enrollment.beneficiaries.map((b) => (
                    <tr key={b.id}>
                      <td>{b.beneficiary_name}</td>
                      <td className="muted">{labelFor(lists, 'relationship', b.relationship)}</td>
                      <td className="muted">{b.allocation_percentage}%</td>
                      <td>
                        <span className={`badge ${b.beneficiary_type === 'primary' ? 'badge-active' : 'badge-neutral'}`}>{b.beneficiary_type}</span>
                      </td>
                      <td className="right">
                        <IconButton icon={IconTrash} label="Remove" variant="danger-ghost" onClick={() => removeBeneficiary(b)} />
                      </td>
                    </tr>
                  ))}
                  {enrollment.beneficiaries.length === 0 && (
                    <tr>
                      <td colSpan={5} className="muted center">
                        No beneficiaries yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <form onSubmit={addBeneficiary} className="form-grid" style={{ marginTop: 16 }}>
              {beneficiaryErrors.general && <div className="alert alert-error span-2">{beneficiaryErrors.general[0]}</div>}
              <label>
                Name *
                <input
                  value={beneficiaryForm.beneficiary_name}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, beneficiary_name: e.target.value })}
                  required
                />
                {beneficiaryErrors.beneficiary_name && <small className="error">{beneficiaryErrors.beneficiary_name[0]}</small>}
              </label>
              <label>
                Relationship *
                <select
                  value={beneficiaryForm.relationship}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, relationship: e.target.value })}
                  required
                >
                  <option value="">—</option>
                  {listOptions(lists, 'relationship').map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {beneficiaryErrors.relationship && <small className="error">{beneficiaryErrors.relationship[0]}</small>}
              </label>
              <label>
                Allocation % *
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={beneficiaryForm.allocation_percentage}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, allocation_percentage: e.target.value })}
                  required
                />
                {beneficiaryErrors.allocation_percentage && <small className="error">{beneficiaryErrors.allocation_percentage[0]}</small>}
              </label>
              <label>
                Type *
                <select
                  value={beneficiaryForm.beneficiary_type}
                  onChange={(e) => setBeneficiaryForm({ ...beneficiaryForm, beneficiary_type: e.target.value as 'primary' | 'contingent' })}
                  required
                >
                  <option value="primary">Primary</option>
                  <option value="contingent">Contingent</option>
                </select>
              </label>
              <div className="form-actions span-2">
                <button className="btn btn-primary">Add beneficiary</button>
              </div>
            </form>
          </>
        )}

        {tab === 'dependents' && (
          <>
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Relationship</th>
                    <th>Coverage Start</th>
                    <th>Coverage End</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {enrollment.dependent_links.map((link) => (
                    <tr key={link.id}>
                      <td>{link.dependent.name}</td>
                      <td className="muted">{labelFor(lists, 'relationship', link.dependent.relationship)}</td>
                      <td className="muted">{link.coverage_start}</td>
                      <td className="muted">{link.coverage_end ?? 'Ongoing'}</td>
                      <td className="right">
                        <IconButton icon={IconTrash} label="Remove" variant="danger-ghost" onClick={() => removeDependentCoverage(link)} />
                      </td>
                    </tr>
                  ))}
                  {enrollment.dependent_links.length === 0 && (
                    <tr>
                      <td colSpan={5} className="muted center">
                        No dependents covered yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <form onSubmit={addDependentCoverage} className="form-grid" style={{ marginTop: 16 }}>
              {dependentErrors.general && <div className="alert alert-error span-2">{dependentErrors.general[0]}</div>}
              <label>
                Dependent *
                <select
                  value={dependentForm.employee_dependent_id}
                  onChange={(e) => setDependentForm({ ...dependentForm, employee_dependent_id: e.target.value })}
                  required
                >
                  <option value="">—</option>
                  {availableDependents.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name}
                    </option>
                  ))}
                </select>
                {dependentErrors.employee_dependent_id && <small className="error">{dependentErrors.employee_dependent_id[0]}</small>}
                {availableDependents.length === 0 && (
                  <small className="muted">This employee has no dependents on file yet — add one from their record first.</small>
                )}
              </label>
              <label>
                Coverage Start *
                <input
                  type="date"
                  value={dependentForm.coverage_start}
                  onChange={(e) => setDependentForm({ ...dependentForm, coverage_start: e.target.value })}
                  required
                />
                {dependentErrors.coverage_start && <small className="error">{dependentErrors.coverage_start[0]}</small>}
              </label>
              <div className="form-actions span-2">
                <button className="btn btn-primary" disabled={availableDependents.length === 0}>
                  Add dependent coverage
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </>
  );
}
